import { Controller } from "@hotwired/stimulus"

// Small media-library picker used by image props in the site builder. It can
// select existing product/theme images or upload a new theme-owned image.
export default class extends Controller {
  static targets = ["input", "panel", "grid", "file", "status", "search"]
  static values = { url: String, messages: Object }

  toggle() {
    this.panelTarget.classList.toggle("hidden")
    if (!this.panelTarget.classList.contains("hidden") && !this.loaded) this.load()
  }

  async load() {
    this.statusTarget.textContent = this.message("loading", "Loading media…")
    try {
      const response = await fetch(`${this.urlValue}.json?per_page=100`, { headers: { Accept: "application/json" } })
      if (!response.ok) throw new Error(`${response.status}`)
      const payload = await response.json()
      this.render(payload.items || [])
      this.loaded = true
    } catch (_error) {
      this.statusTarget.textContent = this.message("load_failed", "Could not load the media library.")
    }
  }

  render(items) {
    this.items = items
    this.renderPhotos(items, this.message("empty", "No images yet. Upload one below."))
  }

  filter(event) {
    const query = event.target.value.trim().toLowerCase()
    const items = (this.items || []).filter((item) => !query || (item.filename || "").toLowerCase().includes(query))
    this.renderPhotos(items, this.message("no_matches", "No matching images."))
  }

  renderPhotos(items, emptyMessage) {
    this.gridTarget.replaceChildren()
    const photos = items.filter((item) => item.media_type === "photo")
    this.statusTarget.textContent = photos.length ? "" : emptyMessage
    photos.forEach((item) => {
      const button = document.createElement("button")
      button.type = "button"
      button.title = item.filename || this.message("select", "Select image")
      button.className = "aspect-square overflow-hidden rounded border border-slate-200 dark:border-slate-600 hover:ring-2 hover:ring-teal-500"
      const image = document.createElement("img")
      image.src = item.thumbnail_url || item.url
      image.alt = item.filename || ""
      image.className = "h-full w-full object-cover"
      button.append(image)
      button.addEventListener("click", () => this.select(item.url))
      this.gridTarget.append(button)
    })
  }

  chooseFile() {
    this.fileTarget.click()
  }

  async upload() {
    const file = this.fileTarget.files[0]
    if (!file) return
    this.statusTarget.textContent = this.message("uploading", "Uploading…")
    const body = new FormData()
    body.append("file", file)

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
        body
      })
      if (!response.ok) throw new Error(`${response.status}`)
      const item = await response.json()
      this.select(item.url)
    } catch (_error) {
      this.statusTarget.textContent = this.message("upload_failed", "Upload failed. Please try again.")
    }
  }

  select(url) {
    this.inputTarget.value = url
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.panelTarget.classList.add("hidden")
  }

  clear() {
    this.select("")
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content
  }

  message(key, fallback) {
    return this.hasMessagesValue ? (this.messagesValue[key] || fallback) : fallback
  }
}
